/**
 * Shared job/step state map: one source of truth for "what does this state
 * mean" (category → color/icon/terminality) instead of hardcoded string
 * lists scattered across components.
 *
 * The map FIRST tries `GET /api/meta` (loadMeta(), called once at startup);
 * on 404 or any failure it keeps the built-in fallback below. Meta also
 * carries the upload cap and poll intervals used as runtime defaults.
 */
import { api, type MetaResponse } from "./api";
import {
  DEFAULT_MAX_UPLOAD_BYTES,
  DEFAULT_POLL_MS,
  DEFAULT_STATS_POLL_MS,
} from "./config";

export type StateCategory =
  | "queued"
  | "running"
  | "waiting"
  | "success"
  | "warning"
  | "failure";

export const STATE_CATEGORIES: StateCategory[] = [
  "queued",
  "running",
  "waiting",
  "success",
  "warning",
  "failure",
];

const TERMINAL_CATEGORIES: ReadonlySet<StateCategory> = new Set([
  "success",
  "warning",
  "failure",
]);

/** Built-in fallback used until /api/meta answers (or forever if it 404s). */
export const BUILTIN_STATE_MAP: Record<string, StateCategory> = {
  PENDING: "queued",
  QUEUED: "queued",
  SUBMITTED: "queued",
  INITIALIZED: "queued",
  RUNNING: "running",
  CLAIMED: "running",
  STARTED: "running",
  AWAITING_APPROVAL: "waiting",
  AWAITING_HUMAN_APPROVAL: "waiting",
  PAUSED: "waiting",
  COMPLETED: "success",
  SUCCEEDED: "success",
  SUCCESS: "success",
  COMPLETED_WITH_CONTROLS: "warning",
  SUCCESS_WITH_CONTROLS: "warning",
  FAILED: "failure",
  REJECTED: "failure",
  VALIDATION_FAILED: "failure",
  CANCELLED: "failure",
};

type Runtime = {
  categories: Record<string, StateCategory>;
  /** Explicit terminal flags from /api/meta (name → terminal). */
  terminalOverrides: Record<string, boolean>;
  meta: MetaResponse | null;
};

const runtime: Runtime = {
  categories: { ...BUILTIN_STATE_MAP },
  terminalOverrides: {},
  meta: null,
};

function isStateCategory(value: string): value is StateCategory {
  return (STATE_CATEGORIES as string[]).includes(value);
}

/** Merge a /api/meta payload into the runtime map (exported for tests). */
export function applyMeta(meta: MetaResponse): void {
  runtime.meta = meta;
  for (const s of meta.job_states ?? []) {
    if (isStateCategory(s.category)) runtime.categories[s.name] = s.category;
    runtime.terminalOverrides[s.name] = s.terminal;
  }
}

/** Reset to the built-in fallback (tests only). */
export function resetStateMap(): void {
  runtime.categories = { ...BUILTIN_STATE_MAP };
  runtime.terminalOverrides = {};
  runtime.meta = null;
}

let metaPromise: Promise<MetaResponse | null> | null = null;

/**
 * Fetch /api/meta once and merge it in. Resolves null when the endpoint is
 * missing (404) or unreachable — the built-in fallback stays active.
 */
export function loadMeta(): Promise<MetaResponse | null> {
  metaPromise ??= api
    .meta()
    .then((meta) => {
      applyMeta(meta);
      return meta;
    })
    .catch(() => null);
  return metaPromise;
}

export function categoryOf(state: string): StateCategory | null {
  return runtime.categories[state] ?? null;
}

export function isTerminal(state: string): boolean {
  const override = runtime.terminalOverrides[state];
  if (override !== undefined) return override;
  const category = categoryOf(state);
  return category !== null && TERMINAL_CATEGORIES.has(category);
}

/* Server-tunable runtime values with local defaults. */
export function maxUploadBytes(): number {
  return runtime.meta?.max_upload_bytes ?? DEFAULT_MAX_UPLOAD_BYTES;
}

/** Whether an upload must be named for the current customer (server-declared; on by default). */
export function requireCustomerSuffix(): boolean {
  return runtime.meta?.upload_require_customer_suffix ?? true;
}

export function pollIntervalMs(): number {
  return runtime.meta?.poll_interval_ms ?? DEFAULT_POLL_MS;
}

/** Whether the model-backed surfaces may be offered.
 *
 * Defaults to FALSE: an unreachable /api/meta, or a server that does not
 * carry the flag, greys the AI out rather than offering a button whose route
 * would refuse. The routes enforce the same switch server-side — this only
 * decides what the operator is shown.
 */
export function aiEnabled(): boolean {
  return runtime.meta?.features?.ai ?? false;
}

export function statsPollIntervalMs(): number {
  return runtime.meta?.stats_poll_interval_ms ?? DEFAULT_STATS_POLL_MS;
}
